export interface SocialAttachmentAPIData {
	category: AttachmentCategory.Social;
	id?: string;
	alt_text?: string;
	gif: string;
	mp4: string;
}

/** Image Attachment (CloudFront Signed URLs) */
export interface ImageAttachmentAPIData {
	category: AttachmentCategory.Image;
	id?: string;
	alt_text?: string;
	gif?: string;
	mp4?: string;
}

export interface VideoAttachmentAPIData {
	category: string;

	id?: string;
	name?: string;
	video_id?: string;
	video_thumbnail_url?: string;
	video_poster_url?: string;
	video_transcript_url?: string;
	video_hd_url?: string;
	video_sd_url?: string;
	video_original_url?: string;
	processing_status?: 'processing' | 'completed' | 'failed' | 'canceled';
}

export interface EmployeeSpotlightAttachmentAPIData {
	category: AttachmentCategory.EmployeeSpotlight;
	id?: string;
	employee_spotlight_id?: string;
	url?: string;
	image_url?: string;
	employee_spotlight_section_id?: string;
	title?: string;
	alt_text?: string;
	width?: number;
	height?: number;
}

export interface LinkPreviewAttachmentAPIData {
	category: AttachmentCategory.LinkPreview;
	id?: string;
	url: string;
	title?: string;
	description?: string;
	image_url?: string;
	site_name?: string;
}

export interface AudioAttachmentAPIData {
	category: AttachmentCategory.Audio;
	id?: string;
	name?: string;
	audio_id?: string;
	audio_url?: string;
	audio_transcript_url?: string;
	audio_transcript?: string;
	audio_quote?: string;
	audio_duration?: number;
	processing_status?: 'processing' | 'completed' | 'failed' | 'canceled';
}

export type AttachmentAPIData =
	| SocialAttachmentAPIData
	| ImageAttachmentAPIData
	| VideoAttachmentAPIData
	| EmployeeSpotlightAttachmentAPIData
	| LinkPreviewAttachmentAPIData
	| AudioAttachmentAPIData;

export enum AttachmentCategory {
	Social = 'social', // uses both image and video to render gifs
	Image = 'image', // Not yet supported
	Video = 'video',
	Audio = 'audio',
	EmployeeSpotlight = 'employee_spotlight',
	LinkPreview = 'link_preview',
}
export abstract class Attachment {
	abstract get category(): AttachmentCategory;
	abstract toJSON(): unknown;
}

export class SocialAttachment extends Attachment {
	constructor(public data: SocialAttachmentAPIData) {
		super();
	}
	get category(): AttachmentCategory {
		return AttachmentCategory.Social;
	}

	get altText(): string | undefined {
		return this.data.alt_text;
	}

	get gif(): string {
		return this.data.gif;
	}

	get mp4(): string {
		return this.data.mp4;
	}

	toJSON(): SocialAttachmentAPIData {
		return {
			category: AttachmentCategory.Social,
			alt_text: this.altText,
			gif: this.gif,
			mp4: this.mp4,
		};
	}
}

export class ImageAttachment extends Attachment {
	constructor(public data: ImageAttachmentAPIData) {
		super();
	}
	get category(): AttachmentCategory {
		return AttachmentCategory.Image;
	}

	get altText() {
		return this.data.alt_text || '';
	}
	// FIXME Consider changing name to something like base_url
	get gif(): string | undefined {
		return this.data.gif;
	}
	// FIXME: Remove mp4 which does not apply to images
	get mp4(): string | undefined {
		return this.data.mp4;
	}

	toJSON(): ImageAttachmentAPIData {
		return {
			category: AttachmentCategory.Image,
			gif: this.gif,
			mp4: this.gif,
		};
	}
}
export class VideoAttachment extends Attachment {
	constructor(public data: VideoAttachmentAPIData) {
		super();
		if (data.video_id) this.data.video_id = data.video_id;
	}

	get category(): AttachmentCategory {
		return AttachmentCategory.Video;
	}

	get posterUrl(): string | undefined {
		return this.data.video_poster_url;
	}

	get altText(): string {
		return String(this.data.name || '');
	}

	get processingStatus(): string | undefined {
		return this.data.processing_status;
	}

	toJSON(): Pick<VideoAttachmentAPIData, 'video_id' | 'category'> {
		return {
			category: AttachmentCategory.Video,
			video_id: this.data.video_id,
		};
	}
}

export class EmployeeSpotlightAttachment extends Attachment {
	constructor(public data: EmployeeSpotlightAttachmentAPIData) {
		super();
	}

	get category(): AttachmentCategory {
		return AttachmentCategory.EmployeeSpotlight;
	}

	get spotlightId(): string | undefined {
		return this.data.employee_spotlight_id || this.data.id;
	}

	get sectionId(): string | undefined {
		return this.data.employee_spotlight_section_id;
	}

	get url(): string | undefined {
		return this.data.url;
	}

	get imageUrl(): string | undefined {
		return this.data.image_url;
	}

	get title(): string | undefined {
		return this.data.title;
	}

	get altText(): string {
		return this.data.alt_text || this.data.title || '';
	}

	get width(): number | undefined {
		return this.data.width;
	}

	get height(): number | undefined {
		return this.data.height;
	}

	toJSON(): {
		category: AttachmentCategory.EmployeeSpotlight;
		employee_spotlight_id: string;
		employee_spotlight_section_id?: string;
	} {
		const spotlightId = this.spotlightId;
		if (!spotlightId) {
			throw new Error('EmployeeSpotlightAttachment is missing required id');
		}
		const sectionId = this.data.employee_spotlight_section_id;
		return {
			category: AttachmentCategory.EmployeeSpotlight,
			employee_spotlight_id: spotlightId,
			...(sectionId ? { employee_spotlight_section_id: sectionId } : {}),
		};
	}
}

export class LinkPreviewAttachment extends Attachment {
	constructor(public data: LinkPreviewAttachmentAPIData) {
		super();
	}

	get category(): AttachmentCategory {
		return AttachmentCategory.LinkPreview;
	}

	get url(): string {
		return this.data.url;
	}

	get title(): string | undefined {
		return this.data.title;
	}

	get description(): string | undefined {
		return this.data.description;
	}

	get imageUrl(): string | undefined {
		return this.data.image_url;
	}

	get siteName(): string | undefined {
		return this.data.site_name;
	}

	get altText(): string {
		return this.data.title || this.data.site_name || 'Link preview';
	}

	toJSON(): LinkPreviewAttachmentAPIData {
		return {
			category: AttachmentCategory.LinkPreview,
			id: this.data.id,
			url: this.url,
			title: this.title,
			description: this.description,
			image_url: this.imageUrl,
			site_name: this.siteName,
		};
	}
}

export class AudioAttachment extends Attachment {
	constructor(public data: AudioAttachmentAPIData) {
		super();
		if (data.audio_id) this.data.audio_id = data.audio_id;
	}

	get category(): AttachmentCategory {
		return AttachmentCategory.Audio;
	}

	get audioUrl(): string | undefined {
		return this.data.audio_url;
	}

	get transcriptUrl(): string | undefined {
		return this.data.audio_transcript_url;
	}

	get transcript(): string | undefined {
		return this.data.audio_transcript;
	}

	get quote(): string | undefined {
		return this.data.audio_quote;
	}

	get duration(): number | undefined {
		return this.data.audio_duration;
	}

	get name(): string | undefined {
		return this.data.name;
	}

	get processingStatus(): string | undefined {
		return this.data.processing_status;
	}

	toJSON(): Pick<AudioAttachmentAPIData, 'audio_id' | 'category'> {
		return {
			category: AttachmentCategory.Audio,
			audio_id: this.data.audio_id,
		};
	}
}
